import { CheckRing } from "@/components/optilase/icons";
import { CtaButton } from "@/components/optilase/cta-button";
import { type ButtonVariant } from "@/components/ui/button";
import { accentOn, headingOn, onSurface, type Surface } from "@/components/optilase/surface";
import { cn } from "@/lib/utils";

export type BookCtaViewProps = {
  heading: string;
  headingAccent?: string;
  points: string[];
  ctaLabel: string;
  ctaVariant?: ButtonVariant;
  ctaHref: string;
  ctaAction?: string | null;
  surface?: Surface | null;
  headingSize?: "large" | "small";
  panel?: Surface | null;
};

export default function BookCtaView({
  heading,
  headingAccent,
  points,
  ctaLabel,
  ctaVariant = "white",
  ctaHref,
  ctaAction,
  surface,
  panel,
  headingSize = "large",
}: BookCtaViewProps) {
  const small = headingSize === "small";
  return (
    <section
      data-outline-skip
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 md:py-12 lg:px-20 lg:py-20", surface ? "" : "bg-white")}
    >
      <div
        style={panel ? { backgroundColor: panel.backgroundColor } : undefined}
        className={cn(
          "mx-auto grid max-w-7xl gap-7 rounded-[20px] px-6 py-9",
          panel ? "" : "bg-teal-08",
          "md:grid-cols-[minmax(0,1fr)_auto] md:gap-x-8 md:gap-y-6 md:px-8 md:py-8 lg:items-center lg:gap-y-4 lg:rounded-3xl lg:px-12 lg:py-10",
          small ? "lg:grid-cols-[minmax(0,841px)_1fr]" : "lg:grid-cols-[minmax(0,780px)_1fr]",
        )}
      >
        <h2
          className={cn(
            // Two even lines rather than a single orphaned word.
            "text-balance md:col-start-1 md:row-start-1 md:max-w-97.5 lg:max-w-none",
            small
              ? "text-xl font-semibold tracking-[-0.01em] md:text-2xl lg:text-[32px] lg:leading-[1.2]"
              : "text-section leading-[1.1]",
            headingOn(panel, "text-white"),
          )}
        >
          {heading}
          {headingAccent ? (
            <span className={cn("block", accentOn(panel, "text-teal-04"))}>{headingAccent}</span>
          ) : null}
        </h2>

        <ul className="flex flex-col gap-3 md:col-span-2 md:row-start-2 md:flex-row md:flex-wrap md:gap-6 lg:col-span-1 lg:col-start-1 lg:gap-4">
          {points.map((p) => (
            <li
              key={p}
              className={cn(
                "flex items-start gap-2 text-base",
                onSurface(panel, "text-white", "text-grey-07", "text-white"),
              )}
            >
              <CheckRing className={cn("mt-[5px] shrink-0", accentOn(panel, "text-white"))} />
              {p}
            </li>
          ))}
        </ul>

        <CtaButton
          href={ctaHref}
          action={ctaAction}
          variant={ctaVariant}
          keepOnMobile
          className="w-full shrink-0 md:col-start-2 md:row-start-1 md:h-12 md:w-48.5 lg:row-span-2 lg:h-14 lg:w-68 lg:self-center lg:justify-self-end"
        >
          {ctaLabel}
        </CtaButton>
      </div>
    </section>
  );
}
